import { createContext, ReactNode, useContext, useState } from 'react';
import { Button as AriaButton, ListBox, ListBoxItem, Separator } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import { CountBadge } from '@/design_system/CountBadge';
import Drawer, { DrawerBody, DrawerHeader } from '@/design_system/Drawer';
import { Footer, FooterButtons } from '@/design_system/Footer';
import { RangeCalendar } from '@/design_system/InputDate';
import { dateStringToCalendarDate } from '@/design_system/InputDate/InputDate';
import { DateRangeValue } from '@/design_system/InputDate/InputDateRange';
import InputSearch, { InputSearchProps } from '@/design_system/InputSearch/InputSearch';
import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import IconCheck from '@/icons/Check.svg';
import IconChevron from '@/icons/Chevron.svg';
import { createBEMClasses } from '@/utils/classname';
import { useLoadMore } from '@/utils/useLoadMore';

import { FilterDateValue } from '../FiltersMenu/FilterDate';
import { Checked, Loader, Unchecked } from '../shared';

import './FiltersDrawer.css';

const { block, element } = createBEMClasses('filters-drawer');

const FiltersDrawerContext = createContext<{
  openedSubMenuId: string | null;
  setOpenedSubMenuId: (id: string | null) => void;
}>({
  openedSubMenuId: null,
  setOpenedSubMenuId: () => {},
});

export const FiltersDrawer = ({
  button,
  isOpen,
  onOpenChange,
  onClearFilters,
  children,
}: {
  button: ReactNode;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => any;
  onClearFilters?: () => void;
  children: ReactNode;
}) => {
  const { t } = useLingui();
  const [openedSubMenuId, setOpenedSubMenuId] = useState<string | null>(null);

  return (
    <Drawer
      position="bottom"
      trigger={button}
      isOpen={isOpen}
      onOpenChange={(newIsOpen) => {
        onOpenChange?.(newIsOpen);
        setOpenedSubMenuId(null);
      }}
      className={block({ submenu: !!openedSubMenuId })}
    >
      <DrawerHeader>
        <Stack row alignItems="center" gap="1rem">
          <Title as="h2">
            <Trans id="filters.filters-drawer.title">Filters</Trans>
          </Title>
          {onClearFilters && (
            <Button variant="link-subtle" onPress={onClearFilters}>
              <Trans id="filters.filters-drawer.clear-filters">Clear filters</Trans>
            </Button>
          )}
        </Stack>
      </DrawerHeader>
      <DrawerBody>
        <FiltersDrawerContext value={{ openedSubMenuId, setOpenedSubMenuId }}>
          {openedSubMenuId ? (
            children
          ) : (
            <ListBox
              aria-label={t({
                id: 'filters-drawer.filters-list.aria-label',
                message: 'Filters list',
              })}
              onAction={(key) => setOpenedSubMenuId(key as string | null)}
              selectionMode="none"
              className={element('menu')}
            >
              {children}
            </ListBox>
          )}
        </FiltersDrawerContext>
      </DrawerBody>
      <Footer>
        <FooterButtons>
          <Button
            type="submit"
            onPress={() => {
              onOpenChange?.(false);
            }}
          >
            <Trans id="filters.filters-drawer.show-results-button">Show results</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </Drawer>
  );
};

export const FiltersSubMenu = ({
  id,
  icon,
  label,
  count,
  children,
}: {
  id: string;
  icon: ReactNode;
  label: string;
  count?: number;
  children: ReactNode;
}) => {
  const { openedSubMenuId, setOpenedSubMenuId } = useContext(FiltersDrawerContext);

  if (openedSubMenuId === null) {
    return (
      <FilterItem
        id={id}
        icon={icon}
        label={label}
        count={count}
        iconRight={<IconChevron right />}
      />
    );
  }

  if (openedSubMenuId !== id) {
    return null;
  }

  return (
    <Stack gap="0.5rem">
      <AriaButton className={element('submenu__header')} onPress={() => setOpenedSubMenuId(null)}>
        <IconChevron left />
        {label}
      </AriaButton>
      {children}
    </Stack>
  );
};

const FiltersListContext = createContext<{
  values: string[];
  onChange: (key: string) => void;
}>({
  values: [],
  onChange: () => {},
});

export function FiltersList<T extends string>({
  ariaLabel,
  values,
  onChange,
  children,
}: {
  ariaLabel: string;
  values: T[];
  onChange: (key: T) => void;
  children: ReactNode;
}) {
  return (
    <ListBox aria-label={ariaLabel} onAction={(key) => onChange(key as T)}>
      <FiltersListContext value={{ values, onChange: onChange as (key: string) => void }}>
        {children}
      </FiltersListContext>
    </ListBox>
  );
}

const FilterItem = ({
  id,
  icon,
  avatar,
  label,
  count,
  iconRight,
  className,
  empty,
  disabled,
}: {
  id: string;
  icon?: ReactNode;
  avatar?: ReactNode;
  label: string;
  count?: number;
  iconRight?: ReactNode;
  className?: string;
  empty?: boolean;
  disabled?: boolean;
}) => {
  return (
    <ListBoxItem
      id={id}
      textValue={label}
      isDisabled={disabled || empty}
      className={element('item', undefined, className)}
    >
      <Stack row gap="0.5rem" alignItems="center" flexWrap="nowrap">
        {icon}
        {avatar}
        <span className="text-ellipsis">{label}</span>
        {!!count && <CountBadge count={count} variant="neutral" size="small" />}
      </Stack>
      {iconRight}
    </ListBoxItem>
  );
};

export const FilterItemNoResults = () => {
  const { t } = useLingui();

  return (
    <FilterItem
      id="empty-mobile"
      empty
      label={t({
        id: 'input-select.no-results',
        message: 'No results',
      })}
    />
  );
};

const FilterRadioItem = ({ id, label }: { id: string; label: string }) => {
  const { values } = useContext(FiltersListContext);

  return <FilterItem id={id} label={label} iconRight={values.includes(id) && <IconCheck />} />;
};

export const FilterCheckboxItem = ({
  id,
  avatar,
  label,
}: {
  id: string;
  avatar?: ReactNode;
  label: string;
}) => {
  const { values } = useContext(FiltersListContext);

  return (
    <FilterItem
      id={id}
      avatar={avatar}
      label={label}
      icon={values.includes(id) ? Checked : Unchecked}
    />
  );
};

export const FilterDivider = () => <Separator className={element('divider')} />;

export const FilterSearch = ({
  inputProps,
  isLoading,
  isLoadingMore,
  onLoadMore,
  ariaLabel,
  values,
  onChange,
  children,
}: {
  inputProps: InputSearchProps;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  ariaLabel: string;
  values: string[];
  onChange: (key: string) => void;
  children: ReactNode;
}) => {
  const loadNextPageTriggerRef = useLoadMore(onLoadMore);

  return (
    <Stack gap="0.5rem">
      <InputSearch {...inputProps} style={{ padding: '0 0.5rem' }} />
      <FiltersList ariaLabel={ariaLabel} values={values} onChange={onChange}>
        {!isLoading && children}
      </FiltersList>
      {(isLoading || isLoadingMore) && <Stack alignItems="center">{Loader}</Stack>}
      <div ref={loadNextPageTriggerRef} className={element('filter-search__end')} />
    </Stack>
  );
};

export const FilterDate = ({
  icon,
  label,
  value,
  customValue,
  onChange,
}: {
  icon: ReactNode;
  label: string;
  value: 'all' | 'last-7-days' | 'last-30-days' | 'custom';
  customValue?: DateRangeValue;
  onChange: (value: FilterDateValue, customValue?: DateRangeValue) => void;
}) => {
  const { t } = useLingui();

  const [isSelectingCustomDate, setIsSelectingCustomDate] = useState(false);

  return (
    <FiltersSubMenu
      id="creation-date"
      icon={icon}
      label={label}
      count={value !== 'all' ? 1 : undefined}
    >
      <FiltersList
        ariaLabel={label}
        values={[value]}
        onChange={(id) => {
          if (id === 'custom') {
            setIsSelectingCustomDate(true);
          } else {
            onChange(id);
          }
        }}
      >
        <FilterRadioItem
          id="all"
          label={t({
            id: 'filters-drawer.filter-date.all',
            message: 'All',
          })}
        />
        <FilterRadioItem
          id="last-7-days"
          label={t({
            id: 'filters-drawer.filter-date.last-7-days',
            message: 'Last 7 days',
          })}
        />
        <FilterRadioItem
          id="last-30-days"
          label={t({
            id: 'filters-drawer.filter-date.last-30-days',
            message: 'Last 30 days',
          })}
        />
        <FilterDivider />
        <FilterRadioItem
          id="custom"
          label={t({
            id: 'filters-drawer.filter-date.custom-date',
            message: 'Custom date',
          })}
        />
      </FiltersList>
      {(isSelectingCustomDate || value === 'custom') && (
        <Stack alignItems="center">
          <RangeCalendar
            value={customValue}
            maxValue={dateStringToCalendarDate(new Date().toISOString())}
            onChange={(value) => {
              if (value) onChange?.('custom', value);
            }}
          />
        </Stack>
      )}
    </FiltersSubMenu>
  );
};
